import { comparer, makeAutoObservable, observable, reaction, runInAction } from "mobx";

import { isApiError, teamMemberships } from "@/api";

import type { AuthModel } from "@/models/auth/AuthModel";
import type { RootStore } from "@/store/RootStore";
import type { TeamsStore } from "@/store/TeamsStore";
import type { LazyStore } from "@/store/types";
import type { IReactionDisposer } from "mobx";

type TeamRef = { uid: string; permalink: string } | null | undefined;

const RETRY_DELAY_MS = 10_000;

const ADMIN_LEVEL_ROLES = ["owner", "admin"];

/**
 * Answers "is this user a student in this team?" for every (team, user)
 * combination in view. Non-classroom teams never have students; in a
 * classroom team everyone counts as a student until the memberships fetch
 * stores the real answer. While the store is observed, one reaction watches
 * the current team, the open doc's team, and the username, and fills the
 * table; every consumer derives synchronously.
 */
export class ClassroomStore implements LazyStore {
  private rootStore: RootStore;

  private auth: AuthModel;

  private teams: TeamsStore;

  /** `${team.uid}:${username}` → whether that user is a student in that team. */
  private studentTable = observable.map<string, boolean>();

  private loadReactionDispose?: IReactionDisposer;

  /** Pending fetch-retry timers, cleared on dispose. */
  private retryTimers = new Set<ReturnType<typeof setTimeout>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.auth = rootStore.auth;
    this.teams = rootStore.teams;
    makeAutoObservable(this);
  }

  lazyEnable() {
    this.loadReactionDispose = reaction(
      () => ({
        username: this.auth.currentUser?.username,
        teams: [this.teams.currentTeam, this.rootStore.doc?.team].flatMap((team) =>
          team && this.isClassroomTeam(team.uid)
            ? [{ uid: team.uid, permalink: team.permalink }]
            : [],
        ),
      }),
      ({ username, teams }) => {
        if (!username) return;
        teams.forEach((team) => this.load(team, username));
      },
      { fireImmediately: true, equals: comparer.structural },
    );
  }

  dispose() {
    this.loadReactionDispose?.();
    this.retryTimers.forEach((timer) => clearTimeout(timer));
    this.retryTimers.clear();
  }

  /**
   * Team UIDs enrolled in the classroom pilot, parsed from
   * `NEXT_PUBLIC_CLASSROOM_TEAM_UIDS` (comma-separated).
   */
  private get teamUids(): string[] {
    return (process.env.NEXT_PUBLIC_CLASSROOM_TEAM_UIDS ?? "")
      .split(",")
      .map((uid) => uid.trim())
      .filter(Boolean);
  }

  /** Whether any team is enrolled in the classroom pilot. */
  get enabled(): boolean {
    return this.teamUids.length > 0;
  }

  /** Whether the team is enrolled in the classroom pilot. */
  isClassroomTeam(teamUid: string | undefined): boolean {
    return !!teamUid && this.teamUids.includes(teamUid);
  }

  /**
   * Whether the current user is a student in the team. False for
   * non-classroom teams and for anonymous users (only members can be
   * students; the server guards everything anyway); fails closed for a
   * classroom team until the memberships fetch stores the answer.
   */
  isStudentIn(team: TeamRef): boolean {
    if (!team || !this.isClassroomTeam(team.uid)) {
      return false;
    }
    const username = this.auth.currentUser?.username;
    if (!username) {
      return false;
    }
    return this.studentTable.get(`${team.uid}:${username}`) ?? true;
  }

  private load(team: { uid: string; permalink: string }, username: string) {
    const key = `${team.uid}:${username}`;
    if (this.studentTable.has(key)) {
      return;
    }

    this.fetchIsStudent(team, username)
      .then((value) => runInAction(() => this.studentTable.set(key, value)))
      .catch((error: unknown) => {
        const status = isApiError(error) ? error.response?.status : undefined;
        if (status === 401 || status === 403) {
          // Memberships are hidden from this user, so they are not a member;
          // non-members only ever see public content.
          runInAction(() => this.studentTable.set(key, false));
          return;
        }
        // Transient failure: the entry stays unknown (fail closed) and is
        // retried shortly.
        const timer = setTimeout(() => {
          this.retryTimers.delete(timer);
          this.load(team, username);
        }, RETRY_DELAY_MS);
        this.retryTimers.add(timer);
      });
  }

  private async fetchIsStudent(
    team: { uid: string; permalink: string },
    username: string,
  ): Promise<boolean> {
    const { memberships } = await teamMemberships(team.permalink);
    const own = memberships?.find((membership) => membership.user.username === username);
    return this.isStudentMember(team.uid, own?.accessLevel);
  }

  /**
   * Whether the access level makes the user a student in a classroom team.
   * Fails closed: an unknown access level counts as a student.
   */
  private isStudentMember(teamUid: string, accessLevel: string | undefined): boolean {
    if (!this.isClassroomTeam(teamUid)) {
      return false;
    }
    return !accessLevel || !ADMIN_LEVEL_ROLES.includes(accessLevel);
  }
}
